/**
 * English UI catalogue for namespace 'lines' (W2-07 lines-panel): the Lines panel, its commands, dialogs, pickers,
 * the status-bar chip, the line info overlay, the setting and the help topics.
 * Imports only src/i18n/define.ts (defineMessages + catalogOf).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

/** Line type names (the panel's Type column and the type filter). */
const TYPE =
  '{type, select, line {Line} helper {Helper} text {Text} image {Image} gif {GIF} paint {Paint} uiMover {UI mover} other {{type}}}';

/** HUD elements a UI mover line moves (RPE attachUI). */
const ELEMENT =
  '{element, select, pause {Pause button} combonumber {Combo number} combo {“COMBO” label} score {Score} bar {Progress bar} name {Song name} level {Level} other {{element}}}';

export const m = defineMessages('lines', {
  // Panel
  panelTitle: 'Lines',
  panelHeading: 'Lines ({total, number})',
  panelHeadingFiltered: 'Lines ({shown, number} of {total, number})',
  treeLabel: '{count, plural, one {# line} other {# lines}}',
  noProjectTitle: 'No chart open',
  noProject: 'Open a chart to see its judge lines.',
  emptyTitle: 'This chart has no lines yet',
  emptyBody: 'Judge lines carry the notes and move them around. Add a line to start.',
  noMatchTitle: 'No line matches',
  noMatchBody: 'No line matches the search and the filters. Clear them to see every line.',
  add: 'Add',
  addMenuLabel: 'Add a line',
  viewLabel: 'Line order',
  viewTree: 'Tree',
  viewOrder: 'Export order',
  searchLabel: 'Search lines by name or number',
  searchPlaceholder: 'Name or #index',
  colToggles: 'Show, solo and lock',
  colName: 'Name',
  colType: 'Type',
  colNotes: 'Notes',
  colNow: 'Now',
  colNowHelp: 'Animating at the playhead. A ghost means Phira draws nothing for the line right now.',
  colZHelp:
    'Draw order (zOrder): higher numbers draw on top. Notes always draw above every line. Ties follow export order.',
  colGroup: 'Group',
  colIndex: '#',
  colIndexHelp: 'Export index (position in the chart file; RPE line number)',
  colZ: 'z',
  notApplicable: '-',
  imageSize: '{w} x {h} px',
  unnamed: 'Unnamed',
  defaultLineName: 'Line {index}',
  expand: 'Show the child lines of {name}',
  collapse: 'Hide the child lines of {name}',
  renameLabel: 'New name of line {index}',
  typeName: TYPE,
  typeUi: `UI: ${ELEMENT}`,
  uiElementName: ELEMENT,
  notesCount: '{count, plural, one {# note} other {# notes}}',
  zLabel: 'Draw order {z}',
  groupDefault: 'Default',
  tagColour: 'Editor colour tag',
  lockedBadge: 'Locked',
  nowAnimating: 'Animating now',
  nowNotDrawn:
    'Not drawn now: {reason, select, alpha {its opacity is 0 or less} scale {its size is 0} emptyText {its text is empty} uiMover {it moves a display element instead} paintCleared {its brush is 0 or less, which clears its painting} other {{reason}}}',
  badgeTurnsWithParent: 'Also turns with its parent',
  badgeUnpin: 'Pinned as an extra lane. Click to unpin.',
  badgeStopAlsoShow: 'Its notes show as ghosts in the lane. Click to stop.',
  badgeFocusHitsounds: 'Hit sounds focused on this line',
  toggleHide: 'Hide in the preview',
  toggleShow: 'Show in the preview',
  toggleSolo: 'Solo in the preview',
  toggleUnsolo: 'Stop solo',
  toggleLock: 'Lock against editing',
  toggleUnlock: 'Unlock',
  contextLabel: 'Line actions',
  dropRoot: 'Drop here to remove the parent',
  previewDiffers: 'Preview differs from export: {details}',
  diffHidden: '{count, plural, one {# line hidden} other {# lines hidden}}',
  diffSolo: '{count, plural, one {# line soloed} other {# lines soloed}}',
  diffMuted: '{count, plural, one {# layer muted} other {# layers muted}}',
  diffDimmed: 'other lines dimmed',
  listJoin: '{a}, {b}',
  showAll: 'Show all',
  lockedCount:
    '{count, plural, one {# line locked} other {# lines locked}} (editing only; the preview and the export are unchanged)',
  alsoShowLabel: 'Also show',
  alsoShowAdd: 'Line...',
  alsoShowRemove: 'Stop showing the notes of {line}',

  // Filter chips
  filtersLabel: 'Line filters',
  chipVisibleNow: 'Visible now',
  chipVisibleNowHelp: 'Lines Phira draws at the playhead (RPE: VisL)',
  chipAnimatingNow: 'Animating now',
  chipAnimatingNowHelp: 'Lines with a property changing at the playhead (RPE: OnEve)',
  chipHasNotes: 'Has notes',
  chipHasNotesHelp: 'Lines that carry notes',
  chipGroup: 'Group',
  chipGroupOne: 'Group: {name}',
  chipGroupMany: '{count, number} groups',
  chipType: 'Type',
  chipTypeMany: '{count, number} types',
  filterOption: '{name} ({count, number})',
  clearFilters: 'Clear filters',

  // Context menu
  menuAdd: 'Add',
  menuDuplicate: 'Duplicate',
  dupWithNotes: 'With notes',
  dupWithoutNotes: 'Without notes',
  dupWithChildren: 'With its child lines',
  dupAsChild: 'As a child of the original',
  menuGroup: 'Group',
  groupNone: 'No group (Default)',
  menuEditGroups: 'Edit groups...',
  menuOrder: 'Order',
  focusHitsoundsOn: 'Hit sounds from these lines only',
  focusHitsoundsOff: 'Hit sounds from every line',
  pinLane: 'Pin as an extra lane',
  unpinLane: 'Unpin the lane',
  alsoShowOn: 'Show their notes in the lane',
  alsoShowOff: 'Stop showing their notes',
  compareLines: 'Compare lines...',
  menuDelete: '{count, plural, one {Delete line} other {Delete # lines}}',

  // Commands
  cmdSwitcher: 'Go to line...',
  cmdShowPanel: 'Show the Lines panel',
  cmdAddLine: 'Add line',
  cmdAddText: 'Add text line',
  cmdAddImage: 'Add image line...',
  cmdAddUiMover: 'Add UI mover line',
  cmdAddGif: 'Add GIF line...',
  cmdAddPaint: 'Add paint line',
  cmdAddHelper: 'Add helper (invisible parent)',
  cmdAddMany: 'New N lines...',
  cmdAddRpe24: 'Add RPE-style 24 lines',
  cmdDuplicate: 'Duplicate line',
  cmdDuplicateActive: 'Duplicate active line',
  cmdDelete: 'Delete line',
  cmdPrev: 'Previous line',
  cmdNext: 'Next line',
  cmdWheelSwitch: 'Switch line with the wheel',
  cmdToFront: 'Bring to front',
  cmdToBack: 'Send to back',
  cmdMoveUp: 'Move up in the export order',
  cmdMoveDown: 'Move down in the export order',
  cmdRename: 'Rename line',
  cmdSetParent: 'Set parent...',
  cmdDetach: 'Remove parent',
  cmdChangeType: 'Change line type...',
  cmdToggleHidden: 'Hide or show lines in the preview',
  cmdToggleSolo: 'Solo lines in the preview',
  cmdToggleLock: 'Lock or unlock lines',
  cmdFocusHitsounds: 'Hit sounds from these lines only',
  cmdShowAll: 'Show all lines (end hide, solo, mute and dim)',
  cmdUnlockAll: 'Unlock all',
  cmdPinLane: 'Pin line as an extra lane',
  cmdAlsoShow: "Show the line's notes in the lane",
  cmdAlsoShowPick: 'Also show the notes of...',
  cmdCopy: 'Copy lines',
  cmdCut: 'Cut lines',
  cmdPaste: 'Paste lines',
  cmdSelectAll: 'Select all lines',
  cmdGroups: 'Edit line groups...',
  cmdSetGroup: 'Put lines in a group',
  cmdNewGroup: 'New line group...',
  cmdToggleView: 'Lines panel: tree view',
  cmdClearFilters: 'Clear the line filters',
  cmdExpandAll: 'Show all child lines',
  cmdCollapseAll: 'Hide all child lines',
  argElement: 'Display element',
  argGroupName: 'Group name',

  // Enablement and refusals
  noLines: 'The chart has no lines.',
  noLineSelected: 'Select a line first (click it in the Lines panel).',
  oneLineOnly: 'Select a single line.',
  noParent: 'The selected lines have no parent.',
  nothingHidden: 'Nothing is hidden, soloed, muted or dimmed.',
  nothingLocked: 'No line is locked.',
  noFiltersOn: 'No filter is on.',
  nothingSelected: 'No line is selected.',
  lockedRefusal:
    '{count, plural, one {“{line}” is locked.} other {# of these lines are locked, including “{line}”.}} Unlock it with the lock icon in the Lines panel to change it.',
  lockedChild:
    '“{line}” is locked, and deleting its parent would change it (it would lose or change its parent). Unlock it with the lock icon in the Lines panel first.',
  lockedDescendant: '“{line}” is locked, so it cannot be deleted with them. Unlock it first or choose another option.',
  wouldLoop: 'Not possible: that would make “{line}” its own ancestor (“{parent}” follows it).',
  lineGone: 'This line no longer exists.',

  // Feedback
  switchedTo: 'Line {line}',
  addedLines: '{count, plural, one {Added # line} other {Added # lines}}',
  deleted: '{count, plural, one {Deleted {line}} other {Deleted # lines}}',
  copied:
    '{count, plural, one {Copied # line} other {Copied # lines}}. Paste {count, plural, one {it} other {them}} with Paste lines in the Lines panel, also in another chart.',
  copiedKey:
    '{count, plural, one {Copied # line} other {Copied # lines}}. Paste {count, plural, one {it} other {them}} with {keys} in the Lines panel, also in another chart.',
  pasted: '{count, plural, one {Pasted # line} other {Pasted # lines}}',
  pasteEmpty: 'The clipboard is empty. Copy lines in the Lines panel first.',
  pasteNotLines: 'The clipboard holds notes or keyframes, not whole lines. Paste them in the lane instead.',
  assetsResolved:
    '{count, plural, one {# file} other {# files}} used by the pasted lines {count, plural, one {was} other {were}} added to this project.',
  notesMoved: '{count, plural, one {Moved # note} other {Moved # notes}} to {line}',
  typeChanged:
    '{line} is now: {type, select, line {Line} text {Text} image {Image} gif {GIF} paint {Paint} uiMover {UI mover} other {{type}}}',

  // Undo labels
  txAddLine:
    'Add {type, select, line {line} helper {helper} text {text line} image {image line} gif {GIF line} paint {paint line} uiMover {UI mover line} other {line}}',
  txAddLines: '{count, plural, one {Add # line} other {Add # lines}}',
  txDuplicate: '{count, plural, one {Duplicate line} other {Duplicate # lines}}',
  txDelete: '{count, plural, one {Delete line} other {Delete # lines}}',
  txSetParent: '{count, plural, one {Set parent} other {Set parent of # lines}}',
  txDetach: '{count, plural, one {Remove parent} other {Remove parent of # lines}}',
  txReorder: '{count, plural, one {Move line in the export order} other {Move # lines in the export order}}',
  txRename: 'Rename line to “{name}”',
  txToFront: '{count, plural, one {Bring line to front} other {Bring # lines to front}}',
  txToBack: '{count, plural, one {Send line to back} other {Send # lines to back}}',
  txSetGroup: '{count, plural, one {Change group of line} other {Change group of # lines}}',
  txNewGroup: 'New group “{name}”',
  txEditGroup: 'Edit group “{name}”',
  txDeleteGroup: 'Delete group “{name}”',
  txPaste: '{count, plural, one {Paste line} other {Paste # lines}}',
  txTurnOn: '{line}: also turn with parent',
  txTurnOff: '{line}: do not turn with parent',
  txChangeType: 'Change type of {line}',
  txMoveNotes: '{count, plural, one {Move # note} other {Move # notes}} to {line}',

  // Common
  cancel: 'Cancel',
  done: 'Done',

  // Delete dialog
  deleteTitle: '{count, plural, one {Delete {name}?} other {Delete # lines?}}',
  deleteNothing: 'These lines no longer exist.',
  deleteNotes:
    '{notes, plural, one {# note} other {# notes}} will be deleted with {count, plural, one {the line} other {the lines}}.',
  deleteNoNotes: '{count, plural, one {The line has} other {The lines have}} no notes.',
  deleteChildren: '{count, plural, one {# child line:} other {# child lines:}}',
  policyDetach: 'Keep them where they are',
  policyDetachHelp: 'They lose their parent (detach) and keep their own numbers.',
  policyReparent: "Attach them to this line's parent",
  policyReparentHelp: 'They follow {parent} instead.',
  policyReparentNone: 'This line has no parent, so they end up without one (like detach).',
  policySubtree: 'Delete them too',
  policySubtreeHelp:
    '{lines, plural, one {# line} other {# lines}} and {notes, plural, one {# note} other {# notes}} more will be deleted.',
  deleteConfirm: '{count, plural, one {Delete line} other {Delete # lines}}',
  deleteUndoable: 'Lines are identified by id, so no other line is renumbered silently. You can undo this.',

  // Parent prompt
  parentTitle: 'Attach {line} to {parent}',
  parentTitleDetach: 'Remove the parent of {line}',
  parentTitleMany: '{count, plural, one {Attach # line to {parent}} other {Attach # lines to {parent}}}',
  parentTitleDetachMany: '{count, plural, one {Remove the parent of # line} other {Remove the parent of # lines}}',
  parentHow: 'How should the line move?',
  parentHowMany: 'How should the lines move? (each line on its own)',
  turnMixed: 'Some of these lines turn with their parent and some do not. Leave the box as it is to keep that.',
  keepNumbers: 'Keep its numbers',
  keepNumbersHelp:
    'Its own position and angle stay as they are, so it moves on screen with its new parent (RPE behaviour).',
  keepPlace: 'Keep it where it is now',
  keepPlaceHelp: "Its position is converted into the parent's frame at the playhead, so it does not jump.",
  keepPlaceAnimated:
    "{line} moves over time: only its position keyframe at or after the playhead is converted into the parent's frame. Check the motion around it afterwards.",
  turnWithParent: 'Also turn with parent',
  turnOffCaption: 'Off: only its position follows',
  turnOnCaption: 'On: its own angle turns too',
  turnSentence:
    "The child's position always circles with the parent's rotation. This only decides whether the child's own angle turns too.",
  notInherited: 'Opacity, colour, size and scroll speed are never inherited from the parent.',
  parentConfirm: 'Attach',
  parentConfirmDetach: 'Remove parent',

  // Line type dialog
  typeTitle: 'Line type of {line}',
  typeDescription: 'Phira decides how to draw a line from its picture and its text, brush and GIF keyframes.',
  typeCurrent: `Now: ${TYPE}`,
  typeHelp:
    '{type, select, line {A plain judge line.} text {Shows text that can change over time.} image {Shows a picture (PNG or JPG for RPE).} gif {Plays a GIF, automatically or along keyframes.} paint {Draws with a brush as it moves.} uiMover {Moves a game display element; has no notes.} other {}}',
  typeApply: `Change to ${TYPE}`,
  typeBlocked: 'Complete the choices above first.',
  typeUnchanged: 'The line already has this type.',
  lossIntro: 'This change removes:',
  lossText: 'the text keyframes ({count, number})',
  lossPaint: 'the brush keyframes ({count, number})',
  lossGifTrack: 'the GIF progress keyframes ({count, number}): Phira cannot load GIF progress on another picture type',
  lossGifAuto: '“Play automatically” of the GIF',
  lossTexture: 'the picture “{path}”',
  lossElement: `the display element (${ELEMENT})`,
  lossUndo: 'You can undo the change.',
  gainAlpha: 'Opacity 100 % is added: without it the display element would be invisible in Phira.',
  gainGifAuto: 'The GIF plays automatically from the start (add GIF progress keyframes to control it).',
  gainText: 'A text track is added.',
  gainPaint: 'A brush track (10 px) is added.',
  pickImage: 'Picture',
  pickGif: 'GIF',
  pickElement: 'Display element',
  noImages: 'This project has no pictures yet.',
  noGifs: 'This project has no GIF that Phira can play.',
  addPicture: 'Add a picture...',
  addPictureHelp: 'Add a PNG or JPG (or a GIF for GIF lines) to the project, then choose it here.',
  addPictureUnavailable: 'Adding pictures is not available right now. Use the Assets panel.',
  initialText: 'Text',
  initialTextPlaceholder: 'Text',
  uiMoverNotes:
    '{line} has {count, plural, one {# note} other {# notes}}. Notes on a line that moves a display element are never drawn: move them to another line first.',
  noOtherLine: 'There is no other line to move them to. Add a line first.',
  moveNotesTo: 'Move to',
  moveNotes: '{count, plural, one {Move # note} other {Move # notes}}',

  // Switcher and pickers
  switcherTitle: 'Go to line',
  switcherInput: 'Line name or number',
  switcherPlaceholder: 'Type a name or #index',
  switcherCount: '{count, plural, =0 {No line matches} one {# line} other {# lines}}',
  switcherNoIndex: 'There is no line #{index}: the chart has {count, plural, one {# line} other {# lines}}.',
  pickParentTitle: '{count, plural, one {Choose a parent} other {Choose a parent for # lines}}',
  pickAlsoShowTitle: "Show another line's notes in the lane",
  pickerNoParent: 'No parent',
  pickerCurrent: 'current',
  pickerWouldLoop: 'would loop',
  pickerActive: 'active line',
  pickerShown: 'shown',

  // New lines
  newLinesTitle: 'New lines',
  newLinesCount: 'How many',
  newLinesCountRange: 'Create between 1 and {max, number} lines at a time.',
  newLinesPattern: 'Names',
  newLinesPatternHelp: "'{k}' counts from 0, '{n}' from 1. Without either, a number is added.",
  newLinesPreview: '{names}{more, plural, =0 {} other { and # more}}',
  newLinesType: 'Type',
  newLinesParent: 'Parent',
  newLinesCreate: '{count, plural, one {Create # line} other {Create # lines}}',
  newImageTitle: 'New image line',
  newImageHelp: 'Choose the picture. Phira centres it on the line position.',
  newGifTitle: 'New GIF line',
  newGifHelp: 'Choose the GIF. It plays automatically until you add GIF progress keyframes.',
  newUiMoverTitle: 'New UI mover line',
  newUiMoverHelp: 'Choose the display element the line moves. The line itself and its notes are not drawn.',

  // Groups
  groupsTitle: 'Line groups',
  groupsDescription: 'Groups are written to the chart (RPE judgeLineGroup). Colours are for the editor only.',
  groupsEmpty: 'No group yet: every line is in “Default”.',
  groupColour: 'Colour of {name}',
  groupNameOf: 'Name of {name}',
  groupLines: '{count, plural, one {# line} other {# lines}}',
  groupShowOnly: 'Show only the lines of {name}',
  groupMoveUp: 'Move {name} up',
  groupMoveDown: 'Move {name} down',
  groupFirst: 'Already the first group.',
  groupLast: 'Already the last group.',
  groupDelete: 'Delete group {name}',
  groupDeleteTitle: 'Delete group “{name}”?',
  groupDeleteBody:
    '{count, plural, one {Its # line goes} other {Its # lines go}} back to “Default”. You can undo this.',
  groupDeleteConfirm: 'Delete group',
  groupDeleted: 'Deleted group “{name}”',
  newGroupName: 'New group',
  newGroupPlaceholder: 'Group name',
  newGroupAdd: 'Add group',
  newGroupNeedsName: 'Type a name first.',
  newGroupWithSelection: '{count, plural, one {Put the selected line in it} other {Put the # selected lines in it}}',

  // Status bar chip
  noActiveLine: 'No line',
  activeChipLabel: 'Active line: {line}. Open the line switcher.',
  activeChipTip: 'Active line: click to switch',

  // Palette (#)
  paletteTitle: '#{index} {name}',
  paletteDetail: `${TYPE}{notes, plural, =0 {} one {, # note} other {, # notes}}{hasGroup, select, yes {, {group}} other {}}`,

  // Setting and help
  settingView: 'Lines panel order',
  settingViewHelp: 'Tree shows parents and children; export order lists the lines as they are written to the chart.',
  helpPanelTitle: 'The Lines panel',
  helpPanelBody:
    'Every judge line of the chart. Click a line to edit it, Ctrl+click or Shift+click to select several. The eye, solo and lock buttons only change the preview and editing, never the export. Filter with the chips or search by name or #index. Press L anywhere to jump to a line.',
  helpParentsTitle: 'Parent lines',
  helpParentsBody:
    "Drag a line onto another to make it follow that line, or use Set parent. A child's position always circles with its parent's rotation; Also turn with parent decides whether its own angle turns too. Opacity, colour, size and speed are never inherited.",
  helpTypesTitle: 'Line types',
  helpTypesBody:
    'Phira draws a line as a plain line, a picture, a GIF, text or a brush depending on its picture and keyframes. A UI mover moves a game display element instead of drawing itself. Change the type with Change line type; the dialog says what the change removes.',
});

export default catalogOf(m);
